import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import dataService from '../services/dataService';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import {
    InboxIcon,
    PaperAirplaneIcon,
    CalendarIcon,
    CurrencyRupeeIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
    CheckBadgeIcon,
    ChatBubbleBottomCenterTextIcon,
    EyeIcon,
    ArrowRightIcon
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

const ThankYouNotes = () => {
    const { user } = useAuth();
    const location = useLocation();
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);
    const [activeTab, setActiveTab] = useState(() => {
        const tabFromState = location.state?.tab;
        return tabFromState === 'sent' || tabFromState === 'received' ? tabFromState : 'received';
    });
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [viewModal, setViewModal] = useState({ isOpen: false, data: null });
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, activeTab]);

    useEffect(() => {
        fetchSettings();
        fetchNotes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchSettings = async () => {
        try {
            const res = await dataService.getOrgSettings();
            if (res.data.success) {
                const orgSettings = typeof res.data.data.settings === 'string'
                    ? JSON.parse(res.data.data.settings || '{}')
                    : (res.data.data.settings || {});
                setSettings(orgSettings);
            }
        } catch (error) {
            console.error('Failed to load settings');
        }
    };

    const fetchNotes = async () => {
        try {
            setLoading(true);
            // const res = await dataService.getThankYouNotes();
            // setNotes(res.data.data);
            // console.log("thanku note ::::",res.data.data)

            const res = await dataService.getThankYouNotes();

            const businessDoneNotes = res.data.data.filter(
                note => note.status == "Business Done"
            );

            setNotes(businessDoneNotes);

            console.log("Business Done Thank You Notes ::::", businessDoneNotes);
        } catch (error) {
            console.error('Failed to load thank you notes', error);
            toast.error('Failed to load thank you notes');
        } finally {
            setLoading(false);
        }
    };

    const activeRole = sessionStorage.getItem('activeRole');
    const isActingAsChapterAdmin = user.role === 'chapter_admin' && activeRole !== 'member';
    const isOrgAdmin = user.role === 'admin';
    const isMember = !isOrgAdmin && !isActingAsChapterAdmin;

    const filteredNotes = notes.filter(note => {
        let matchesTab = true;

        if (isActingAsChapterAdmin) {
            const userChapter = String(user.chapter || '').trim().toLowerCase();
            const senderChap = String(note.sender_chapter || '').trim().toLowerCase();
            const recipientChap = String(note.recipient_chapter || '').trim().toLowerCase();

            if (activeTab === 'received') {
                if (recipientChap !== userChapter) matchesTab = false;
            } else if (activeTab === 'sent') {
                if (senderChap !== userChapter) matchesTab = false;
            }
        } else if (isMember) {
            const isReceived = Number(note.referred_to) === Number(user.id);
            const isSent = Number(note.user_id) === Number(user.id);

            if (activeTab === 'received' && !isReceived) matchesTab = false;
            if (activeTab === 'sent' && !isSent) matchesTab = false;
        }

        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = (
            (note.sender_name || '').toLowerCase().includes(searchLower) ||
            (note.recipient_name || note.referred_to || '').toLowerCase().includes(searchLower) ||
            (note.reference_name || '').toLowerCase().includes(searchLower)
        );

        return matchesTab && matchesSearch;
    });

    const itemsPerPage = 12;
    const totalPages = Math.ceil(filteredNotes.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedNotes = filteredNotes.slice(startIndex, endIndex);

    return (
        <div className="space-y-6 h-full flex flex-col animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-black text-gray-900 tracking-tight italic">Thank You Notes</h1>
                    <p className="text-sm font-bold text-gray-500 uppercase tracking-widest mt-1">Confirmed business success within the community</p>
                </div>
            </div>

            {/* Controls */}
            <div className="bg-white p-6 rounded-[2.5rem] shadow-2xl shadow-gray-200/50 border border-gray-100 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                {(isActingAsChapterAdmin || isMember) && (
                    <div className="flex p-1.5 bg-gray-50 rounded-2xl w-full lg:w-fit border border-gray-100/50">
                        <button
                            onClick={() => setActiveTab('received')}
                            className={clsx(
                                "flex items-center gap-2 px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all active:scale-95",
                                activeTab === 'received' ? "bg-white text-gray-900 shadow-md border border-gray-100" : "text-gray-400 hover:text-gray-600"
                            )}
                        >
                            <InboxIcon className="w-4 h-4" />
                            Received {isActingAsChapterAdmin ? '(to Chapter Members)' : 'Notes'}
                        </button>
                        <button
                            onClick={() => setActiveTab('sent')}
                            className={clsx(
                                "flex items-center gap-2 px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all active:scale-95 ml-1",
                                activeTab === 'sent' ? "bg-white text-gray-900 shadow-md border border-gray-100" : "text-gray-400 hover:text-gray-600"
                            )}
                        >
                            <PaperAirplaneIcon className="w-4 h-4" />
                            Sent {isActingAsChapterAdmin ? '(by Chapter Members)' : 'Notes'}
                        </button>
                    </div>
                )}

                <div className="relative flex-1 max-w-md">
                    <MagnifyingGlassIcon className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by name, company or note..."
                        className="w-full bg-gray-50 border border-transparent rounded-2xl pl-12 pr-12 py-4 text-sm focus:outline-none focus:ring-4 focus:ring-gray-100 focus:bg-white focus:border-gray-200 transition-all shadow-inner"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            {/* Content Area */}
            {loading ? (
                <div className="flex flex-1 items-center justify-center py-20 bg-white rounded-[2.5rem] shadow-sm border border-gray-100/50">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-100 border-t-yellow-500"></div>
                </div>
            ) : (
                <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-gray-200/50 border border-gray-100 overflow-hidden flex-1 flex flex-col">
                    {filteredNotes.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center py-20 text-gray-400">
                            <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mb-6">
                                <ChatBubbleBottomCenterTextIcon className="w-10 h-10 opacity-20" />
                            </div>
                            <p className="text-xl font-black uppercase tracking-widest opacity-30 italic px-8 text-center">No thank you notes found</p>
                        </div>
                    ) : (
                        <>
                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {paginatedNotes.map((note) => (
                                        <div
                                            key={note.id}
                                            className="group relative bg-white border-2 border-slate-100 rounded-[2rem] p-6 hover:border-primary-200 hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
                                        >
                                            <div>
                                                {/* Header with Flow Indicator */}
                                                <div className="flex items-center justify-between gap-3 mb-6">
                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                        <div className="flex flex-col min-w-0">
                                                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Sender</span>
                                                            <span className="text-sm font-black text-slate-900 truncate">{note.sender_name}</span>
                                                            <span className="text-[10px] font-bold text-orange-600 uppercase tracking-wider truncate">{note.sender_chapter || 'N/A'}</span>
                                                        </div>
                                                        <ArrowRightIcon className="w-4 h-4 text-slate-300 shrink-0 mx-1 animate-pulse" />
                                                        <div className="flex flex-col min-w-0">
                                                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Recipient</span>
                                                            <span className="text-sm font-black text-slate-900 truncate">{note.recipient_name || note.referred_to}</span>
                                                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider truncate">{note.recipient_chapter || 'N/A'}</span>
                                                        </div>
                                                    </div>
                                                    <div className="p-2.5 bg-primary-50 rounded-2xl text-primary-600 shrink-0">
                                                        <ChatBubbleBottomCenterTextIcon className="w-5 h-5" />
                                                    </div>
                                                </div>

                                                {/* Details Grid */}
                                                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50/50 rounded-2xl border border-slate-100 mb-6">
                                                    <div>
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                            Date Submitted
                                                        </p>
                                                        <div className="flex flex-col">
                                                            <span className="text-xs font-bold text-slate-700">
                                                                {new Date(note.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                            </span>
                                                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                                                                {new Date(note.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                                                            Amount
                                                        </p>
                                                        <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 rounded-xl border border-emerald-100">
                                                            <CurrencyRupeeIcon className="w-3.5 h-3.5 text-emerald-600" />
                                                            <span className="text-xs font-black text-emerald-900 tracking-tight">
                                                                {parseFloat(note.business_done_amount || 0).toLocaleString('en-IN')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Actions Footer */}
                                            {(user?.role !== 'chapter_admin' || settings?.chapter_referral_thirdparty_data !== true) && (
                                                <div className="flex justify-end pt-4 border-t border-slate-50">
                                                    <button
                                                        onClick={() => setViewModal({ isOpen: true, data: note })}
                                                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-50 hover:bg-slate-900 hover:text-white rounded-xl font-bold text-xs transition-all active:scale-95 text-slate-600 border border-slate-100 hover:border-slate-900"
                                                    >
                                                        <EyeIcon className="w-4 h-4" />
                                                        View Note
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {/* Pagination Controls */}
                            <div className="flex flex-col md:flex-row items-center justify-between px-8 py-5 border-t border-slate-100 gap-4 bg-gray-50/50">
                                <div className="text-sm font-semibold text-slate-600">
                                    Showing <span className="font-black text-slate-900">{Math.min(endIndex, filteredNotes.length)}</span> of{' '}
                                    <span className="font-black text-slate-900">{filteredNotes.length}</span> {filteredNotes.length === 1 ? 'note' : 'notes'}
                                </div>

                                {filteredNotes.length > itemsPerPage && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                            className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${currentPage === 1
                                                ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-transparent'
                                                : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                                }`}
                                        >
                                            Previous
                                        </button>

                                        <div className="flex items-center gap-1">
                                            {[...Array(totalPages)].map((_, index) => {
                                                const pageNum = index + 1;
                                                if (
                                                    pageNum === 1 ||
                                                    pageNum === totalPages ||
                                                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                                ) {
                                                    return (
                                                        <button
                                                            key={pageNum}
                                                            onClick={() => setCurrentPage(pageNum)}
                                                            className={`w-10 h-10 rounded-xl font-black text-sm transition-all ${currentPage === pageNum
                                                                ? 'bg-[#4eb7f5] text-white shadow-lg shadow-blue-100'
                                                                : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                                                }`}
                                                        >
                                                            {pageNum}
                                                        </button>
                                                    );
                                                } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                                    return <span key={pageNum} className="text-slate-400 px-1">...</span>;
                                                }
                                                return null;
                                            })}
                                        </div>

                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage === totalPages}
                                            className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${currentPage === totalPages
                                                ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-transparent'
                                                : 'bg-white border border-slate-200 text-slate-700 hover:border-[#4eb7f5] hover:bg-blue-50 active:scale-95'
                                                }`}
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}


            {(mounted && viewModal.isOpen) && createPortal(
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-300">
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto relative border border-gray-100">
                        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 px-6 py-6 text-white relative">
                            <button
                                onClick={() => setViewModal({ isOpen: false, data: null })}
                                className="absolute top-6 right-6 p-2 rounded-xl bg-white/20 hover:bg-white/30 transition-all text-white border border-white/20 active:scale-90"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                            <div className="flex items-center gap-4">
                                <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-md">
                                    <CheckBadgeIcon className="w-6 h-6" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-black italic tracking-tight">Thank You Note</h2>
                                    <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">Reference Success</p>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-2 gap-x-6 gap-y-6">
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1 px-1">Source / Sender</label>
                                    <p className="text-sm font-black text-gray-900 px-4 py-2.5 bg-gray-50 rounded-xl border border-gray-100">{viewModal.data?.sender_name} ({viewModal.data?.sender_chapter || 'N/A'})</p>
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1 px-1">Recipient / Receiver</label>
                                    <p className="text-sm font-black text-gray-900 px-4 py-2.5 bg-gray-50 rounded-xl border border-gray-100">{viewModal.data?.recipient_name || viewModal.data?.referred_to || 'N/A'} ({viewModal.data?.recipient_chapter || 'N/A'})</p>
                                </div>
                                <div className="col-span-2 p-6 bg-emerald-50 rounded-[2rem] border-2 border-emerald-100 flex items-center justify-between">
                                    <div>
                                        <label className="text-[10px] font-black text-emerald-600 uppercase tracking-widest block mb-1">Total Business Amount</label>
                                        <div className="flex items-center gap-1.5 text-2xl font-black text-emerald-900">
                                            <CurrencyRupeeIcon className="w-8 h-8" />
                                            {parseFloat(viewModal.data.business_done_amount || 0).toLocaleString('en-IN')}
                                        </div>
                                    </div>
                                    <div className="h-14 w-14 bg-emerald-500 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-200 rotate-6 transition-transform hover:rotate-0">
                                        <CheckBadgeIcon className="w-12 h-12 text-white" />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block px-1">Note Context</label>
                                <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 relative shadow-inner">
                                    <div className="absolute top-2 left-4 text-4xl text-gray-200 font-serif leading-none italic">“</div>
                                    <p className="text-sm font-medium text-gray-700 leading-relaxed px-2 py-1 italic relative z-10">
                                        {viewModal.data.description || 'Confirmed business transaction completed successfully.'}
                                    </p>
                                    <div className="absolute bottom-2 right-4 text-4xl text-gray-200 font-serif leading-none italic rotate-180">“</div>
                                </div>
                            </div>

                            <div className="pt-2 flex justify-between items-center border-t border-gray-50 uppercase tracking-tighter">
                                <div className="flex items-center gap-2 text-gray-400 text-[10px] font-bold">
                                    <CalendarIcon className="w-3.5 h-3.5" />
                                    System Timestamp: {new Date(viewModal.data.created_at).toLocaleString()}
                                </div>
                                <p className="text-[10px] font-black text-emerald-600">Verified Read-Only</p>
                            </div>

                            <button
                                onClick={() => setViewModal({ isOpen: false, data: null })}
                                className="w-full py-3 bg-gray-900 text-white rounded-xl text-[11px] font-black uppercase tracking-widest hover:bg-gray-800 transition-all active:scale-[0.98] shadow-lg shadow-gray-200"
                            >
                                Close Note
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default ThankYouNotes;
